import { useEffect, useRef, useState } from 'react';
import { baseUrl } from './useFetch';

export interface AssetReturnReminder {
    assignmentId: number;
    employeeName: string;
    employeeEmail: string | null;
    assetName: string;
    assetCode: string;
    assetBrand: string | null;
    categoryName: string;
    subCategoryName: string | null;
    expectedReturnDate: string;   // "yyyy-MM-dd"
    isOverdue: boolean;
    isDueToday: boolean;
    daysOverdue: number;
    daysLeft: number;
}

export interface AssetNotification {
    type: 'new_request' | 'return_due_today' | 'return_approaching' | 'recovery_pending';
    message: string;
    detail: string;
    count: number;
    key: string;
    reminder?: AssetReturnReminder;
}

export const useAssetNotification = (token: string | undefined, enabled: boolean = true) => {
    const [notification, setNotification] = useState<AssetNotification | null>(null);

    // For new_request polling (counts)
    const lastPendingRef = useRef<number | null>(null);
    const isFirstPollRef = useRef<boolean>(true);

    // For return reminders — queue of items to cycle through
    const reminderQueueRef = useRef<AssetReturnReminder[]>([]);
    const reminderIndexRef = useRef<number>(0);
    const shownReminderIdsRef = useRef<Set<number>>(new Set());

    // General dismiss tracking
    const dismissedRef = useRef<Set<string>>(new Set());

    // ── Fetch pending requests count (existing endpoint) ──────────────────────
    const checkPendingRequests = async () => {
        if (!token || !enabled) return;
        try {
            const res = await fetch(`${baseUrl}Asset/asset-activity-summary`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) return;
            const data = await res.json();
            const pending: number = data.pendingRequestsCount ?? 0;
            const pendingRecoveryCount: number = data.pendingRecoveryCount ?? 0;
            const pendingRecoveryAmount: number = data.pendingRecoveryAmount ?? 0;
            const isFirst = isFirstPollRef.current;
            isFirstPollRef.current = false;

            if (!isFirst && lastPendingRef.current !== null && pending > lastPendingRef.current) {
                const diff = pending - lastPendingRef.current;
                const key = `new_request-${pending}`;
                if (!dismissedRef.current.has(key)) {
                    setNotification({
                        type: 'new_request',
                        message: `${diff} new asset request${diff > 1 ? 's' : ''} submitted`,
                        detail: `Total pending: ${pending} requests awaiting action`,
                        count: diff,
                        key
                    });
                }
            }

            if (isFirst && pending > 0) {
                const key = `new_request-first-${new Date().toDateString()}-${pending}`;
                if (!dismissedRef.current.has(key)) {
                    setNotification({
                        type: 'new_request',
                        message: `${pending} asset request${pending > 1 ? 's' : ''} pending your action`,
                        detail: 'Click to review and process pending asset requests',
                        count: pending,
                        key
                    });
                }
            }

            // Notification for Pending Asset Recovery (Point 17)
            if (pendingRecoveryCount > 0) {
                const recoveryKey = `recovery-pending-${new Date().toDateString()}-${pendingRecoveryCount}-${pendingRecoveryAmount}`;
                if (!dismissedRef.current.has(recoveryKey)) {
                    setNotification({
                        type: 'recovery_pending',
                        message: `Asset Recovery Pending — ₹${pendingRecoveryAmount.toLocaleString('en-IN')}`,
                        detail: `${pendingRecoveryCount} asset recovery case${pendingRecoveryCount > 1 ? 's' : ''} awaiting settlement / final settlement action`,
                        count: pendingRecoveryCount,
                        key: recoveryKey
                    });
                }
            }

            lastPendingRef.current = pending;
        } catch {
            // non-critical
        }
    };

    // ── Fetch return reminders (detailed endpoint) ────────────────────────────
    const checkReturnReminders = async () => {
        if (!token || !enabled) return;
        try {
            const res = await fetch(`${baseUrl}Asset/return-reminders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) return;                        // 403 = no permission, just skip
            const data: AssetReturnReminder[] = await res.json();
            if (!data || data.length === 0) return;

            // Only load assets we haven't shown yet into the queue
            const newItems = data.filter(
                r => !shownReminderIdsRef.current.has(r.assignmentId)
            );

            if (newItems.length > 0) {
                reminderQueueRef.current = newItems;
                reminderIndexRef.current = 0;
                showNextReminder();
            }
        } catch {
            // non-critical
        }
    };

    // Show the next item from the reminder queue
    const showNextReminder = () => {
        const queue = reminderQueueRef.current;
        const idx = reminderIndexRef.current;
        if (idx >= queue.length) return;

        const item = queue[idx];
        const type: AssetNotification['type'] = item.isOverdue ? 'return_due_today' : 'return_approaching';
        const key = `return-${item.assignmentId}-${new Date().toDateString()}`;

        if (!dismissedRef.current.has(key)) {
            setNotification({
                type,
                message: item.isOverdue
                    ? `⚠️ Overdue — ${item.employeeName}`
                    : item.isDueToday
                        ? `🔔 Due Today — ${item.employeeName}`
                        : `📅 Return in ${item.daysLeft} day${item.daysLeft !== 1 ? 's' : ''} — ${item.employeeName}`,
                detail: `${item.assetName} (${item.assetCode})${item.isOverdue ? ` · ${item.daysOverdue} day${item.daysOverdue > 1 ? 's' : ''} overdue` : ''}`,
                count: 1,
                key,
                reminder: item
            });
            shownReminderIdsRef.current.add(item.assignmentId);
        } else {
            // Already dismissed — skip to next
            reminderIndexRef.current += 1;
            showNextReminder();
        }
    };

    const dismissNotification = () => {
        if (!notification) return;
        dismissedRef.current.add(notification.key);
        setNotification(null);

        // If it was a return reminder, advance to next in queue
        if (notification.reminder) {
            reminderIndexRef.current += 1;
            // Small delay so the dismiss animation plays before next one appears
            setTimeout(() => showNextReminder(), 600);
        }
    };

    useEffect(() => {
        if (!token || !enabled) return;

        // Stagger the two checks slightly so they don't race
        checkPendingRequests();
        setTimeout(() => checkReturnReminders(), 3000);

        const pendingInterval = setInterval(checkPendingRequests, 60 * 1000);
        const reminderInterval = setInterval(checkReturnReminders, 5 * 60 * 1000); // every 5 min

        return () => {
            clearInterval(pendingInterval);
            clearInterval(reminderInterval);
        };
    }, [token, enabled]);

    return { assetNotification: notification, dismissAssetNotification: dismissNotification };
};